import 'moment/locale/fr';
import {
  AppService,
  CyclabilityZone,
  ExportData,
  ExportDataProjection,
  ExportDataService,
  Partner,
  Period,
  PoiCategory,
  PoiCategoryService,
  Report,
  ReportType,
  ReportTypeService,
  User,
  UserService,
  last12Months,
} from '@geovelo-frontends/commons';
import { ThemeProvider } from '@mui/material/styles';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Map } from 'maplibre-gl';
import moment, { Moment } from 'moment';
import { SnackbarProvider } from 'notistack';
import { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import './tracking';

import { environment } from '../environment';
import useZoneStats from '../hooks/use-zone-stats';
import { ICountingSite, ICountingSourceState, TCountingDisplayMode } from '../pages/counts/types';

import { AppContext, world, worldId } from './context';
import i18n, { defaultLanguage } from './i18n';
import Router from './router';
import { TTab } from './router/tabs';
import theme from './theme';

Chart.register(
  CategoryScale,
  LinearScale,
  LineController,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
);

Chart.defaults.font.family = '"Roboto", "Helvetica", "Arial", sans-serif';

function App(): React.JSX.Element {
  const [map, setMap] = useState<Map>();
  const [currentUser, setCurrentUser] = useState<User | null>();
  const [userPartners, setUserPartners] = useState<Partner[] | null>();
  const [exportDataTypes, setExportDataTypes] = useState<ExportData[]>();
  const [exportDataProjections, setExportDataProjections] = useState<ExportDataProjection[]>();
  const [signInDialogOpen, openSignInDialog] = useState(false);
  const [zoneMap, setZoneMap] = useState<{ [id: number]: CyclabilityZone }>({ [worldId]: world });
  const [zoneCodeMap, setZoneCodeMap] = useState<{ [key: string]: number | null }>({});
  const [zoneParentMap, setZoneParentMap] = useState<{ [id: number]: number | null }>({
    [worldId]: null,
  });
  const [zoneChildrenMap, setZoneChildrenMap] = useState<{ [id: number]: number[] | null }>({});
  const [currentZone, setCurrentZone] = useState<CyclabilityZone | null>();
  const [statsPeriods, setStatsPeriods] = useState<Array<{ key: string; date: Moment }>>();
  const [selectedStatsKey, selectStatsKey] = useState<string | null>(null);
  const [selectedStatsIndex, selectStatsIndex] = useState<number | null>(null);
  const [countryCode, setCountryCode] = useState<string | null>();
  const [zoneCode, setZoneCode] = useState<string | null>();
  const [reports, setReports] = useState<Report[]>();
  const [reportTypes, setReportTypes] = useState<ReportType[]>();
  const [allReportTypes, setAllReportTypes] = useState<ReportType[]>();
  const [selectedReport, setSelectedReport] = useState<Report | null | undefined>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<Period>(last12Months);
  const [poiCategories, setPoiCategories] = useState<PoiCategory[]>();
  const [currentTab, setCurrentTab] = useState<TTab>('stats');
  const [contributionId, setContributionId] = useState<number | null>(null);
  const [countingSites, setCountingSites] = useState<ICountingSite[]>([]);
  const [countingSources, setCountingSources] = useState<ICountingSourceState[]>([]);
  const [countingDisplayMode, setCountingDisplayMode] = useState<TCountingDisplayMode>('sites');
  const [selectedCountingSiteId, setSelectedCountingSiteId] = useState<string | null>(null);
  const [countingLoading, setCountingLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const {
    byZone: zoneStats,
    canLoadOlder: canLoadOlderStats,
    loadingOlder: loadingOlderStats,
    loadOlder: loadOlderStats,
  } = useZoneStats({
    currentZone,
    zoneMap,
    childrenMap: zoneChildrenMap,
    periods: statsPeriods,
    selectedKey: selectedStatsKey,
    selectedIndex: selectedStatsIndex,
    setStatsPeriods,
    selectStatsKey,
    selectStatsIndex,
  });

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (currentUser) {
      getUserData();
    } else if (currentUser === null) {
      setUserPartners(null);
    }
  }, [currentUser]);

  useEffect(() => {
    if (userPartners) {
      const partner = userPartners[0];
      if (partner && environment.config.enableSHPDownload) getExportData(partner);
    } else {
      setExportDataTypes(undefined);
      setExportDataProjections(undefined);
    }
  }, [userPartners]);

  async function init() {
    AppService.environment = environment;

    moment.locale(defaultLanguage);

    const user = await UserService.getCurrentUser();
    setCurrentUser(user);

    getReportTypes();
    getPoiCategories();

    setInitialized(true);
  }

  async function getReportTypes() {
    try {
      const _types = await ReportTypeService.getReportTypes();
      const types = _types.flatMap((reportType) => [reportType, ...reportType.children]);

      setReportTypes(types.filter(({ isBindToOSM }) => isBindToOSM));
      setAllReportTypes(types);
    } catch (err) {
      console.error(err);
    }
  }

  async function getPoiCategories() {
    try {
      setPoiCategories(await PoiCategoryService.getPoiCategories());
    } catch (err) {
      console.error(err);
    }
  }

  async function getUserData() {
    try {
      setUserPartners(
        (await UserService.getPartners()).filter(
          ({ code, dashboardTabsPermissions: { documentsSend } }) =>
            documentsSend === 'write' &&
            (!environment.config.partnerCode || environment.config.partnerCode === code),
        ),
      );
    } catch (err) {
      console.error(err);
    }
  }

  async function getExportData(partner: Partner) {
    try {
      setExportDataTypes(
        (await ExportDataService.getExportData(partner)).filter(
          ({ type: { code: typeCode }, model: { code: modelCode } }) =>
            ['BICYCLE_FACILITIES'].includes(typeCode) &&
            ['DATAGOUV_3.1', 'DATA_GOUV_Z30', 'DATA_GOUV', 'idfm'].includes(modelCode),
        ),
      );
      setExportDataProjections(await ExportDataService.getExportDataProjections(partner));
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <AppContext.Provider
      value={{
        map: { current: map },
        user: { current: currentUser, partners: userPartners, signInDialogOpen },
        zone: {
          map: zoneMap,
          codeMap: zoneCodeMap,
          parentMap: zoneParentMap,
          childrenMap: zoneChildrenMap,
          current: currentZone,
        },
        stats: {
          periods: statsPeriods,
          selectedKey: selectedStatsKey,
          selectedIndex: selectedStatsIndex,
          byZone: zoneStats,
          canLoadOlder: canLoadOlderStats,
          loadingOlder: loadingOlderStats,
          exportDataTypes,
          exportDataProjections,
        },
        report: {
          reports,
          types: reportTypes,
          allTypes: allReportTypes,
          selectedReport,
          selectedPeriod,
        },
        pois: { categories: poiCategories },
        counts: {
          displayMode: countingDisplayMode,
          sites: countingSites,
          selectedSiteId: selectedCountingSiteId,
          sources: countingSources,
          loading: countingLoading,
        },
        routing: { countryCode, zoneCode, currentTab, contributionId },
        actions: {
          setMap,
          setCurrentUser,
          openSignInDialog,
          setZoneMap,
          setZoneCodeMap,
          setZoneParentMap,
          setZoneChildrenMap,
          setCurrentZone,
          setStatsPeriods,
          selectStatsKey,
          selectStatsIndex,
          loadOlderStats,
          setCountryCode,
          setZoneCode,
          setCurrentTab,
          setContributionId,
          setReports,
          setSelectedReport,
          setSelectedPeriod,
          setCountingSites,
          setCountingSources,
          setCountingDisplayMode,
          setSelectedCountingSiteId,
          setCountingLoading,
        },
      }}
    >
      <I18nextProvider i18n={i18n}>
        <ThemeProvider theme={theme}>
          <SnackbarProvider
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            maxSnack={1}
          >
            {initialized && (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <Router />
              </LocalizationProvider>
            )}
          </SnackbarProvider>
        </ThemeProvider>
      </I18nextProvider>
    </AppContext.Provider>
  );
}

export default App;
